import { useCallback, useEffect, useMemo, useState } from "react";

export interface UseRowSelectionReturn<T> {
	selectedKeys: T[];
	selectedCount: number;
	allSelectedMode: boolean;
	allSelected: boolean;
	someSelected: boolean;
	noneSelected: boolean;
	toggleAll: () => void;
	toggleRow: (key: T) => void;
	isSelected: (key: T) => boolean;
	deselectAll: () => void;
}

export function useRowSelection<T>(
	totalCount: number,
	equals: (a: T, b: T) => boolean,
): UseRowSelectionReturn<T> {
	const [allSelectedMode, setAllSelectedMode] = useState(false);
	const [keys, setKeys] = useState<T[]>([]);

	const selectedCount = useMemo(() => allSelectedMode
		? totalCount - keys.length
		: keys.length, [keys.length, totalCount, allSelectedMode]);

	const allSelected = useMemo(() => {
		return selectedCount === totalCount && totalCount > 0;
	}, [selectedCount, totalCount]);

	const noneSelected = useMemo(() => selectedCount === 0, [selectedCount]);

	const someSelected = useMemo(() => {
		if (allSelected) return false;

		return !noneSelected;
	}, [allSelected, noneSelected]);

	const isSelected = useCallback(
		(key: T) => {
			const found = keys.some((k) => equals(k, key));
			return allSelectedMode ? !found : found;
		},
		[allSelectedMode, keys, equals],
	);

	useEffect(() => {
		window.dispatchEvent(new Event('resize'));
	}, [allSelected])

	const toggleRow = useCallback(
		(key: T) => {
			setKeys((prev) => {
				const idx = prev.findIndex((k) => equals(k, key));
				if (idx >= 0) {
					const next = prev.slice();
					next.splice(idx, 1);
					return next;
				}
				return [...prev, key];
			});

			// Handle the "all selected, now toggling one off" case using current values
			if (selectedCount === totalCount && !allSelectedMode) {
				setAllSelectedMode(true);
				setKeys([key]); // the one being deselected
			}
		},
		[selectedCount, totalCount, allSelectedMode, equals],
	);

	const toggleAll = useCallback(() => {
		if (allSelectedMode) {
			// Currently in all-selected mode — deselect all
			setAllSelectedMode(false);
			setKeys([]);
		} else if (keys.length > 0) {
			// Some selected in normal mode — deselect all
			setKeys([]);
		} else {
			// None selected — select all
			setAllSelectedMode(true);
			setKeys([]);
		}
	}, [allSelectedMode, keys.length]);

	const deselectAll = useCallback(() => {
		setAllSelectedMode(false);
		setKeys([]);
	}, []);

	const selectedKeys = keys;

	return {
		selectedKeys,
		selectedCount,
		allSelectedMode,
		allSelected,
		someSelected,
		noneSelected,
		toggleAll,
		toggleRow,
		isSelected,
		deselectAll,
	};
}